/**
 * Group the resolved keybinding table for the shortcuts page (Figma
 * `9229:190120`, content `9229:190276`).
 *
 * One section per declaring module, in the order the table already has. The
 * platform's own chords are the "Shell" block. A module section takes its
 * commands' category when they all share one; otherwise the module id, which
 * is the only name still known.
 */
import { PLATFORM_MODULE_ID } from '@hibro/shared';

export interface ShortcutListRow {
  readonly id: string;
  readonly moduleId: string;
  readonly label: string;
  readonly chord: string;
  /** Translated command category, when the command declared one. */
  readonly category?: string;
}

export interface ShortcutSection {
  readonly moduleId: string;
  readonly title: string;
  readonly rows: readonly ShortcutListRow[];
}

export function groupShortcutRows(
  rows: readonly ShortcutListRow[],
  labels: { readonly shell: string },
): readonly ShortcutSection[] {
  const sections: ShortcutSection[] = [];
  const indexByModule = new Map<string, number>();
  for (const row of rows) {
    const existing = indexByModule.get(row.moduleId);
    if (existing === undefined) {
      indexByModule.set(row.moduleId, sections.length);
      sections.push({
        moduleId: row.moduleId,
        title: '',
        rows: [row],
      });
      continue;
    }
    const section = sections[existing];
    if (section) {
      sections[existing] = { ...section, rows: [...section.rows, row] };
    }
  }
  return sections.map((section) => ({
    ...section,
    title: sectionTitle(section, labels.shell),
  }));
}

function sectionTitle(section: ShortcutSection, shell: string): string {
  if (section.moduleId === PLATFORM_MODULE_ID) {
    return shell;
  }
  const categories = section.rows.map((row) => row.category);
  const named = categories.filter(
    (category): category is string =>
      category !== undefined && category.length > 0,
  );
  const [only] = named;
  if (only !== undefined && named.length === categories.length) {
    const shared = named.every((category) => category === only);
    if (shared) {
      return only;
    }
  }
  return section.moduleId;
}
